interface ItemWithId {
  id?: string;
  [key: string]: any;
}

/**
 * Merges old and new partial objects with IDs:
 * - All new items are included (with or without ID)
 * - Removed items (in old but not in new) are marked with _destroy: true
 *
 * @param oldItems - Original items
 * @param newItems - Updated items
 * @returns Array of all new items + deleted items, or null if no changes
 */
function mergeAndDestroyPartialObjects<T extends ItemWithId>(
  oldItems?: T[] | null,
  newItems?: T[] | null,
): (T | { id: string; _destroy: true })[] | null {
  const oldValues = oldItems || [];
  const newValues = newItems || [];

  const result: (T | { id: string; _destroy: true })[] = [];

  // Create a map of new values for efficient lookup
  const newValuesMap = new Map<string, T>();
  newValues.forEach((item) => {
    if (item.id) {
      newValuesMap.set(item.id, item);
    }
  });

  // Add all new items to the result
  result.push(...newValues);

  // Find items in old values that are not in new values - mark for deletion
  // Send all fields except 'position' for deleted items
  oldValues.forEach((oldItem) => {
    if (oldItem.id && !newValuesMap.has(oldItem.id)) {
      const { id } = oldItem;
      result.push({ id, _destroy: true });
    }
  });

  if (result.length === 0) return null;
  return result;
}

export default mergeAndDestroyPartialObjects;
